import PropTypes from "prop-types";
import {
    buildInsuranceInvoiceSection,
    capitalizeChargeDescription,
    formatInvoicePhone,
    normalizePaymentEvents,
} from "../../../lib/reservationInvoiceHelpers";
import { formatDate, formatDate2 } from "./formatUtils";

const InvoiceSection = ({ title, children, className = "" }) => (
    <section className={`rounded-2xl border border-[#f0f0f0] bg-white p-4 sm:p-5 ${className}`}>
        {title ? (
            <h3 className="fontSemi mb-3 text-xs uppercase tracking-[0.08em] text-[#9b9ba5]">{title}</h3>
        ) : null}
        {children}
    </section>
);

InvoiceSection.propTypes = {
    title: PropTypes.string,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};

const InfoRow = ({ label, value }) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    return (
        <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
            <span className="fontRegular shrink-0 text-sm text-[#667085] sm:min-w-[7.5rem]">{label}</span>
            <span className="fontRegular min-w-0 break-words text-sm text-[#2F2F46]">{value}</span>
        </div>
    );
};

InfoRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const SummaryMoneyRow = ({ label, amount, emphasize = false, negative = false }) => (
    <div
        className={`flex items-start justify-between gap-3 py-1.5 ${
            emphasize ? "border-t border-[#E6E6E3] pt-3 fontSemi text-[#2F2F46]" : ""
        }`}>
        <span className="min-w-0 flex-1 text-sm text-[#667085]">{label}</span>
        <span className={`shrink-0 text-sm ${emphasize ? "text-base text-[#5D5DF6]" : "text-[#2F2F46]"}`}>
            {negative ? "-" : ""}${Number(amount || 0).toFixed(2)}
        </span>
    </div>
);

SummaryMoneyRow.propTypes = {
    label: PropTypes.string.isRequired,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    emphasize: PropTypes.bool,
    negative: PropTypes.bool,
};

const InsuranceBlock = ({ insurance, t }) => {
    if (!insurance.hasInsurance) {
        return <p className="fontRegular text-sm text-[#667085]">{t("invoice.noInsurance")}</p>;
    }

    return (
        <div className="space-y-2 text-sm leading-relaxed text-[#2F2F46]">
            <InfoRow label={t("invoice.provider")} value={insurance.providerLabel} />
            <InfoRow label={t("invoice.carrier")} value={insurance.carrier} />
            <InfoRow label={t("invoice.policyNumber")} value={insurance.policyNumber} />
            {insurance.coverageLines.map((line, i) => (
                <p key={i} className="fontRegular text-sm text-[#2F2F46]">
                    {line.label}: {line.detail}
                </p>
            ))}
            {insurance.addonSections?.map((section, si) => (
                <div key={si} className="rounded-xl bg-[#F2F2FA] p-3">
                    <p className="fontSemi text-sm text-[#2F2F46]">{section.addonName}</p>
                    {section.lines.map((line, li) => (
                        <p key={li} className="fontRegular mt-1 text-sm text-[#667085]">
                            {line.label}
                        </p>
                    ))}
                </div>
            ))}
        </div>
    );
};

InsuranceBlock.propTypes = {
    insurance: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const SignatureBlock = ({ signatures, t }) => {
    const label = t("invoice.renterSignature");
    const url = signatures?.signature1;

    return (
        <div>
            <p className="fontRegular mb-2 text-sm font-medium text-[#2F2F46]">{label}</p>
            {url ? (
                <img
                    src={url}
                    alt={label}
                    className="max-h-24 w-full max-w-md rounded-lg border border-[#f0f0f0] bg-white p-2 object-contain"
                />
            ) : (
                <div className="h-10 max-w-md border-b border-[#2F2F46]" />
            )}
        </div>
    );
};

SignatureBlock.propTypes = {
    signatures: PropTypes.object,
    t: PropTypes.func.isRequired,
};

const ChargeSummarySection = ({ charges, taxes, invoice, discountAmount, t }) => (
    <InvoiceSection title={t("invoice.chargeSummary")}>
        <ul className="space-y-3">
            {charges.map((inv, i) => (
                <li key={i} className="border-b border-[#EEF0F6] pb-3 last:border-b-0 last:pb-0">
                    <div className="flex items-start justify-between gap-3">
                        <p className="fontRegular min-w-0 flex-1 text-sm leading-snug text-[#2F2F46]">
                            {capitalizeChargeDescription(inv?.description)}
                        </p>
                        <p className="fontSemi shrink-0 text-sm text-[#2F2F46]">${Number(inv?.total || 0).toFixed(2)}</p>
                    </div>
                    <p className="fontRegular mt-1 text-xs text-[#667085]">
                        {t("invoice.units")}: {inv?.unit ?? "—"} · {t("invoice.rate")}: $
                        {Number(inv?.rate || 0).toFixed(2)}
                    </p>
                </li>
            ))}
        </ul>

        <div className="mt-4 space-y-0.5 border-t border-[#E6E6E3] pt-3">
            <SummaryMoneyRow label={t("invoice.estimatedCharges")} amount={invoice?.estimatedTotal} />
            <SummaryMoneyRow label={t("invoice.discount")} amount={discountAmount} negative />
            {taxes.map((inv, i) => (
                <SummaryMoneyRow
                    key={i}
                    label={
                        inv.taxType === "$"
                            ? `${inv?.taxName}($${inv?.total})`
                            : `${inv?.taxName}(${inv.taxValue}%)`
                    }
                    amount={inv?.total}
                />
            ))}
            <SummaryMoneyRow label={t("invoice.total")} amount={invoice?.total} emphasize />
            <SummaryMoneyRow label={t("invoice.paid")} amount={invoice?.deposit} />
            <SummaryMoneyRow label={t("invoice.due")} amount={invoice?.due} emphasize />
        </div>
    </InvoiceSection>
);

ChargeSummarySection.propTypes = {
    charges: PropTypes.array.isRequired,
    taxes: PropTypes.array.isRequired,
    invoice: PropTypes.object.isRequired,
    discountAmount: PropTypes.number.isRequired,
    t: PropTypes.func.isRequired,
};

const ReservationInvoiceDocument = ({
    resDetails,
    customerDetails,
    carDetails,
    insurancePolicyData,
    myCompanyData,
    stepper,
    t,
}) => {
    const invoice = resDetails?.invoice || {};
    const charges = invoice.charges || [];
    const taxes = invoice.taxes || [];
    const insurance = buildInsuranceInvoiceSection(resDetails, insurancePolicyData);
    const payments = normalizePaymentEvents(invoice);
    const discountAmount = Number(resDetails?.discount?.amount ?? 0);
    const authAmount = resDetails?.isPaid
        ? Number(invoice.deposit ?? resDetails.amount - discountAmount).toFixed(2)
        : "0.00";
    const paymentLabel = resDetails?.isPaid
        ? payments[0]?.paymentPlatform || payments[0]?.eventType || "Paid"
        : t("invoice.unpaid");

    const additionalDriver = resDetails?.additionalDriver;
    const hasDriver = additionalDriver?.name && additionalDriver.name !== "";
    const vehicleVin = resDetails?.carSelected?.VIN || carDetails?.VIN || "—";
    const reservationId = resDetails?.id || "—";
    const rentalTitle = stepper >= 4 ? t("invoice.rentalOut") : t("invoice.reservationLabel");

    const renterPhone =
        formatInvoicePhone(customerDetails?.mobileNumber) !== "—"
            ? formatInvoicePhone(customerDetails?.mobileNumber)
            : formatInvoicePhone(resDetails?.mobileNumber);

    const customerName = resDetails?.customerName || "—";
    const carName =
        resDetails?.carSelected?.name ||
        resDetails?.carSelected?.carName ||
        carDetails?.carName ||
        carDetails?.config?.make ||
        "—";
    const plateNumber = resDetails?.carSelected?.carNumber || carDetails?.carNumber || "—";
    const makeAndModel = carDetails?.config?.make || carDetails?.config?.model || "—";

    return (
        <div className="reservation-invoice-document w-full min-w-0 space-y-4 sm:space-y-5">
            <div className="rounded-2xl border border-[#f0f0f0] bg-white p-4 sm:p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 text-sm leading-relaxed text-[#2F2F46]">
                        <p className="fontSemi text-base text-[#2F2F46]">{myCompanyData?.name}</p>
                        <p className="fontRegular mt-1 text-[#667085]">{myCompanyData?.address?.address}</p>
                        <p className="fontRegular text-[#667085]">
                            {myCompanyData?.address?.city}, {myCompanyData?.address?.country}
                        </p>
                        <p className="fontRegular text-[#667085]">
                            PH# {formatInvoicePhone(myCompanyData?.phone)}
                        </p>
                    </div>
                    <div className="min-w-0 shrink-0 text-left sm:text-right">
                        <span className="inline-flex rounded-full bg-[#5D5DF6]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#5D5DF6]">
                            {rentalTitle}
                        </span>
                        <p className="fontSemi mt-2 text-lg text-[#2F2F46]">
                            {t("invoice.reservationId")}: {reservationId}
                        </p>
                        <div className="fontRegular mt-3 space-y-1.5 text-sm text-[#667085]">
                            <p>
                                <span className="fontSemi text-[#2F2F46]">{t("invoice.dateTimeOut")}:</span>{" "}
                                {formatDate2(new Date(resDetails?.pickupDate))}{" "}
                                {formatDate(new Date(resDetails?.pickupDate))}
                            </p>
                            <p>
                                <span className="fontSemi text-[#2F2F46]">{t("invoice.dateTimeIn")}</span>{" "}
                                {formatDate2(new Date(resDetails?.dropoffDate))}{" "}
                                {formatDate(new Date(resDetails?.dropoffDate))}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <ChargeSummarySection
                charges={charges}
                taxes={taxes}
                invoice={invoice}
                discountAmount={discountAmount}
                t={t}
            />

            <div className="grid gap-4 lg:grid-cols-2">
                <InvoiceSection title={t("invoice.renterInfo")}>
                    <div className="space-y-2">
                        <p className="fontSemi text-base text-[#2F2F46]">{customerName}</p>
                        <InfoRow label="Email" value={customerDetails?.email || resDetails?.email} />
                        <InfoRow label="PH" value={renterPhone} />
                    </div>
                </InvoiceSection>

                <InvoiceSection title={t("invoice.unitDetails")}>
                    <div className="space-y-2">
                        <InfoRow label="Car Name" value={carName} />
                        <InfoRow label={t("plateNumber")} value={plateNumber} />
                        <InfoRow label="Make & Model" value={makeAndModel} />
                        <InfoRow label={t("invoice.vin")} value={vehicleVin} />
                    </div>
                </InvoiceSection>
            </div>

            {hasDriver ? (
                <InvoiceSection title={t("invoice.additionalDrivers")}>
                    <div className="space-y-2 text-sm text-[#2F2F46]">
                        <InfoRow label="Name" value={additionalDriver.name} />
                        <InfoRow label="License#" value={additionalDriver.license?.number} />
                        {additionalDriver.license?.issueDate ? (
                            <p className="fontRegular text-sm text-[#667085]">
                                Issue date: {formatDate2(new Date(additionalDriver.license.issueDate))}, Exp. Date:{" "}
                                {formatDate2(new Date(additionalDriver.license.expDate))}
                            </p>
                        ) : null}
                    </div>
                </InvoiceSection>
            ) : null}

            <InvoiceSection title={t("invoice.billTo")}>
                <div className="space-y-2">
                    <InfoRow label={t("invoice.paymentTypeLabel")} value={paymentLabel} />
                    <InfoRow label={t("invoice.auth")} value={`$${authAmount}`} />
                    <InfoRow label="Payment intent" value={resDetails?.paymentIntent} />
                </div>
            </InvoiceSection>

            <InvoiceSection title={t("invoice.insurance")}>
                <InsuranceBlock insurance={insurance} t={t} />
            </InvoiceSection>

            <InvoiceSection>
                <p className="fontRegular text-sm leading-relaxed text-[#667085]">{t("invoice.disclaimer")}</p>
                <p className="fontRegular mt-4 text-sm leading-relaxed text-[#667085]">{t("invoice.waiverNotice")}</p>
            </InvoiceSection>

            <InvoiceSection title={t("invoice.renterSignature")}>
                <SignatureBlock signatures={resDetails?.signatures} t={t} />
            </InvoiceSection>
        </div>
    );
};

ReservationInvoiceDocument.propTypes = {
    resDetails: PropTypes.object,
    customerDetails: PropTypes.object,
    carDetails: PropTypes.object,
    insurancePolicyData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    myCompanyData: PropTypes.object,
    stepper: PropTypes.number,
    t: PropTypes.func.isRequired,
};

export default ReservationInvoiceDocument;
